import { useState, useEffect, useRef } from "react";
import { Search, X, Building2, Coins, FileText, BookOpen, TrendingUp, Sparkles, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { newsHref } from "@/components/news/types";

interface SearchResult {
  type: "broker" | "crypto" | "news" | "glossary" | "education";
  id: number | string;
  title: string;
  description?: string;
  link: string;
}

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: brokers } = useQuery<any[]>({
    queryKey: ["/api/brokers"],
  });

  const { data: exchanges } = useQuery<any[]>({
    queryKey: ["/api/crypto-exchanges"],
  });

  const { data: articles } = useQuery<any[]>({
    queryKey: ["/api/news"],
  });

  const { data: glossary } = useQuery<any[]>({
    queryKey: ["/api/glossary"],
  });

  const { data: courses } = useQuery<any[]>({
    queryKey: ["/api/education"],
  });

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const getResults = (): SearchResult[] => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const results: SearchResult[] = [];

    brokers?.forEach((b: any) => {
      if (b.name?.toLowerCase().includes(q) || b.description?.toLowerCase().includes(q)) {
        results.push({
          type: "broker",
          id: b.id,
          title: b.name,
          description: b.description?.slice(0, 100),
          link: `/broker/${b.slug || b.id}`,
        });
      }
    });

    exchanges?.forEach((e: any) => {
      if (e.name?.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q)) {
        results.push({
          type: "crypto",
          id: e.id,
          title: e.name,
          description: e.description?.slice(0, 100),
          link: `/crypto/${e.slug}`,
        });
      }
    });

    articles?.forEach((a: any) => {
      if (a.title?.toLowerCase().includes(q) || a.content?.toLowerCase().includes(q)) {
        results.push({
          type: "news",
          id: a.id,
          title: a.title,
          description: a.excerpt?.slice(0, 100),
          link: newsHref(a),
        });
      }
    });

    glossary?.forEach((g: any) => {
      if (g.term?.toLowerCase().includes(q) || g.definition?.toLowerCase().includes(q)) {
        results.push({
          type: "glossary",
          id: g.id,
          title: g.term,
          description: g.definition?.slice(0, 100),
          link: `/forexpedia#${g.term?.toLowerCase().replace(/\s+/g, "-")}`,
        });
      }
    });

    courses?.forEach((c: any) => {
      if (c.title?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q)) {
        results.push({
          type: "education",
          id: c.id,
          title: c.title,
          description: c.description?.slice(0, 100),
          link: `/education`,
        });
      }
    });

    return results.slice(0, 10);
  };

  const results = getResults();

  const getIcon = (type: string) => {
    switch (type) {
      case "broker": return <Building2 className="h-4 w-4 text-primary" />;
      case "crypto": return <Coins className="h-4 w-4 text-orange-400" />;
      case "news": return <FileText className="h-4 w-4 text-blue-400" />;
      case "glossary": return <BookOpen className="h-4 w-4 text-green-400" />;
      case "education": return <TrendingUp className="h-4 w-4 text-purple-400" />;
      default: return <Search className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "broker": return "Broker";
      case "crypto": return "Exchange";
      case "news": return "News";
      case "glossary": return "Forexpedia";
      case "education": return "Education";
      default: return type;
    }
  };

  return (
      <Dialog open={open} onOpenChange={setOpen}>
      {/*
        Icon only — the "Search" word is gone, so the control is a 40px circle that
        matches the account avatar next to it. The word was the last piece of printed
        label in that column, and the magnifier is unambiguous without it; the label
        moves to `aria-label` so screen readers keep the name. Clicking still opens the
        same search dialog. (`asChild` means the trigger's only child must be the button
        itself, so this note lives here rather than inside it.)
      */}
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          aria-label="Search"
          className="h-10 w-10 shrink-0 rounded-full border border-white/10 bg-white/[0.06] p-0 text-gray-300 transition-all hover:border-primary/40 hover:bg-white/10 hover:text-white"
        >
          <Search className="h-4 w-4 shrink-0 text-primary" />
        </Button>
      </DialogTrigger>
      <DialogContent className="top-2 max-h-[calc(100vh-1rem)] w-[calc(100vw-0.75rem)] max-w-[720px] translate-y-0 overflow-hidden rounded-[1.25rem] border border-white/10 bg-[#0a101f] p-0 shadow-[0_30px_90px_rgba(0,0,0,0.55)] sm:top-[10%] sm:max-h-[calc(100vh-6.5rem)] sm:w-[calc(100vw-1rem)] sm:rounded-[1.5rem]">
        <DialogHeader className="sr-only">
          <DialogTitle>Search BullBuzz</DialogTitle>
          <DialogDescription>Search brokers, exchanges, news, and glossary terms</DialogDescription>
        </DialogHeader>
        <div className="border-b border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.01))] px-3 pb-3 pt-4 sm:px-5 sm:pb-4 sm:pt-5">
          <div className="mb-3">
            <div className="hidden items-center gap-2 text-[10px] font-black uppercase tracking-[0.26em] text-primary sm:flex">
              <Sparkles className="h-3.5 w-3.5" />
              Smart Search
            </div>
            <div className="text-base font-black tracking-tight text-white sm:mt-1 sm:text-xl">
              <span className="sm:hidden">Search BullBuzz</span>
              <span className="hidden sm:inline">Find brokers, exchanges, news and education fast</span>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-3 shadow-inner shadow-black/10 sm:gap-3 sm:px-4">
            <Search className="h-4 w-4 shrink-0 text-gray-400 sm:h-5 sm:w-5" />
            <Input
              ref={inputRef}
              placeholder="Search brokers, exchanges, news..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-12 border-0 bg-transparent px-0 text-sm text-white placeholder:text-gray-500 focus-visible:ring-0 sm:h-14 sm:text-base"
            />
            {query ? (
              <Button variant="ghost" size="sm" className="h-9 w-9 rounded-full p-0 text-gray-400 hover:bg-white/5 hover:text-white" onClick={() => setQuery("")}>
                <X className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>

        <div className="max-h-[calc(100vh-9.5rem)] overflow-y-auto px-3 py-3 sm:max-h-[calc(100vh-13rem)] sm:px-4 sm:py-4">
          {query && results.length === 0 && (
            <div className="rounded-2xl border border-white/8 bg-white/[0.03] px-5 py-10 text-center text-gray-500">
              <div className="text-base font-bold text-white">No results found for "{query}"</div>
              <div className="mt-2 text-sm text-gray-400">Try a broker name, exchange, category, or keyword.</div>
            </div>
          )}

          {results.length > 0 ? (
            <div className="flex flex-col gap-4 sm:gap-3">
              {results.map((result) => (
                <Link
                  key={`${result.type}-${result.id}`}
                  href={result.link}
                  className="block"
                  onClick={() => setOpen(false)}
                >
                  <div className="group flex items-start gap-4 rounded-2xl border border-white/8 bg-white/[0.03] p-[18px] transition-all hover:border-primary/25 hover:bg-white/[0.05] cursor-pointer sm:gap-4 sm:p-4">
                    <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-[#10182b] sm:h-10 sm:w-10">
                      {getIcon(result.type)}
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:gap-2">
                        <span className="min-w-0 text-[15px] font-bold leading-snug text-white line-clamp-2 sm:flex-1 sm:truncate sm:text-base">{result.title}</span>
                        <span className="shrink-0 rounded-full bg-white/5 px-2 py-0.5 text-[9px] uppercase tracking-wider text-gray-500 sm:text-[10px]">
                          {getTypeLabel(result.type)}
                        </span>
                      </div>
                      {result.description && (
                        <p className="text-[13px] leading-6 text-gray-400 line-clamp-3 sm:text-sm sm:leading-relaxed sm:line-clamp-2">{result.description}</p>
                      )}
                    </div>
                    <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-gray-500 transition-colors group-hover:text-primary" />
                  </div>
                </Link>
              ))}
            </div>
          ) : null}

          {!query && (
            <div className="sm:hidden rounded-2xl border border-primary/15 bg-primary/10 p-4">
              <div className="text-sm font-black text-white">Start typing to search</div>
              <div className="mt-1 text-xs leading-relaxed text-gray-300">Find brokers, exchanges, news, glossary terms, and education content.</div>
            </div>
          )}

          {!query && (
            <div className="hidden gap-3 sm:grid sm:grid-cols-2">
              {[
                { title: "Brokers", desc: "Search regulated forex brokers and reviews.", icon: <Building2 className="h-4 w-4 text-primary" /> },
                { title: "Crypto Exchanges", desc: "Find exchange fees, features and ratings.", icon: <Coins className="h-4 w-4 text-orange-400" /> },
                { title: "Market News", desc: "Jump to recent headlines and detailed analysis.", icon: <FileText className="h-4 w-4 text-blue-400" /> },
                { title: "Learning", desc: "Open glossary entries and education guides.", icon: <BookOpen className="h-4 w-4 text-green-400" /> },
              ].map((item) => (
                <div key={item.title} className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-[#10182b]">{item.icon}</div>
                  <div className="mt-3 font-black tracking-tight text-white">{item.title}</div>
                  <div className="mt-1 text-sm leading-relaxed text-gray-400">{item.desc}</div>
                </div>
              ))}
              <div className="rounded-2xl border border-primary/15 bg-primary/10 p-4 sm:col-span-2">
                <div className="text-sm font-black text-white">Start typing to search</div>
                <div className="mt-1 text-sm leading-relaxed text-gray-300">Search brokers, crypto exchanges, news articles, glossary terms, and education content from one place.</div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
